import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Loader } from 'ncoded-component-library';
import { EnterpriseMember, EnterpriseSeniority } from 'models/Enterprises';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useMemberSeniorities from 'hooks/useMemberSeniorities';
import { isSingleElementArray } from 'utils';
import AddNewOption from 'components/AddNewOption';
import EditOption from 'components/EditOption';
import EditEntepriseValueModal from 'components/EditEntepriseValueModal';
import { FormApi } from 'final-form';
import api from 'api';
import DeleteMemberSeniorityModal from 'components/DeleteMemberSeniorityModal';

import './MemberSenioritiesForm.styles.scss';

type MemberSenioritiesFormProps = {
  className?: string;
  enterpriseId: string;
  memberSeniorities: EnterpriseSeniority[];
  updateMemberSeniorities: (
    updatedMemberSeniorities: EnterpriseSeniority[],
    shouldRefetch?: boolean,
  ) => void;
};

const MemberSenioritiesForm: React.FC<MemberSenioritiesFormProps> = (props) => {
  const {
    className,
    enterpriseId,
    memberSeniorities,
    updateMemberSeniorities,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [unitId, setUnitId] = useState('');
  const [newValue, setNewValue] = useState('');
  const [oldValue, setOldValue] = useState('');
  const [
    enterpriseMembersWithSeniorities,
    setEnterpriseMembersWithSeniorities,
  ] = useState<EnterpriseMember[]>([]);

  const { popServerError } = useContext(PopNotificationsContext);

  const [editSeniorityModal, editSeniorityModalRef] =
    useCallbackRef<ModalRef>();
  const [deleteSeniorityModal, deleteSeniorityModalRef] =
    useCallbackRef<ModalRef>();

  const { sortedMemberSeniorities } = useMemberSeniorities(memberSeniorities);

  const showDelete = useMemo(
    () => !isSingleElementArray(memberSeniorities),
    [memberSeniorities],
  );

  const classes = classNames('member-seniorities-form', className);

  const { t } = useTranslation();

  const getInitialValues = useCallback(
    (_id: string, name: string) => ({ _id, name }),
    [],
  );

  const resetStates = useCallback(() => {
    setUnitId('');
    setNewValue('');
    setOldValue('');
    setEnterpriseMembersWithSeniorities([]);
  }, []);

  const onAdd = useCallback(
    async ({ name }, formApi: FormApi) => {
      try {
        setIsLoading(true);
        const { data } = await api.enterprises.createMemberSeniority(
          enterpriseId,
          name,
        );
        updateMemberSeniorities([...memberSeniorities, data], false);
        formApi.reset();
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [enterpriseId, memberSeniorities, popServerError, updateMemberSeniorities],
  );

  const editValue = useCallback(
    async (seniorityId: string, name: string) => {
      try {
        setIsLoading(true);
        await api.enterprises.updateMemberSeniority(seniorityId, name);
        setEditingValue('');
        updateMemberSeniorities(
          memberSeniorities.map((seniority) =>
            seniority._id === seniorityId ? { ...seniority, name } : seniority,
          ),
        );
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [memberSeniorities, popServerError, updateMemberSeniorities],
  );

  const onEdit = useCallback(
    async ({ _id, name }) => {
      try {
        setIsLoading(true);

        const { data: enterpriseMembersWithSeniority } =
          await api.enterprises.getEnterpriseMembersForSeniority(_id);

        if (enterpriseMembersWithSeniority.length) {
          setEnterpriseMembersWithSeniorities(enterpriseMembersWithSeniority);
          setOldValue(
            memberSeniorities.find((seniority) => seniority._id === _id).name,
          );
          setNewValue(name);
          setUnitId(_id);
          setIsLoading(false);
          editSeniorityModal?.open();
        } else {
          editValue(_id, name);
        }
      } catch (e) {
        popServerError(e);
        setIsLoading(false);
      }
    },
    [editSeniorityModal, editValue, memberSeniorities, popServerError],
  );

  const onEditConfirm = useCallback(
    () => editValue(unitId, newValue),
    [editValue, newValue, unitId],
  );

  const onDelete = useCallback(
    async (_id) => {
      try {
        setIsLoading(true);

        const { data: enterpriseMembersWithSeniority } =
          await api.enterprises.getEnterpriseMembersForSeniority(_id);

        if (enterpriseMembersWithSeniority.length) {
          setUnitId(_id);
          setEnterpriseMembersWithSeniorities(enterpriseMembersWithSeniority);
          deleteSeniorityModal?.open();
        } else {
          const { data } = await api.enterprises.deleteMemberSeniority(_id);
          updateMemberSeniorities(data, false);
        }
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [deleteSeniorityModal, popServerError, updateMemberSeniorities],
  );

  return (
    <div className={classes}>
      {isLoading && <Loader />}

      {sortedMemberSeniorities.map(({ _id, name }) => (
        <EditOption
          key={_id}
          value={name}
          isEditing={editingValue === _id}
          showDelete={showDelete}
          initialValues={getInitialValues(_id, name)}
          onPencilClick={() => setEditingValue(_id)}
          onCancel={() => setEditingValue('')}
          onDelete={() => onDelete(_id)}
          onEdit={onEdit}
        />
      ))}

      <AddNewOption onAdd={onAdd} />

      <EditEntepriseValueModal
        ref={editSeniorityModalRef}
        enterpriseMembersWithValue={enterpriseMembersWithSeniorities}
        dataType={t('seniority')}
        oldValue={oldValue}
        newValue={newValue}
        onClose={resetStates}
        onConfirm={onEditConfirm}
      />

      <DeleteMemberSeniorityModal
        ref={deleteSeniorityModalRef}
        memberSeniorities={memberSeniorities}
        enterpriseSeniorityId={unitId}
        enterpriseMembersWithSeniorities={enterpriseMembersWithSeniorities}
        onClose={resetStates}
        updateMemberSeniorities={updateMemberSeniorities}
      />
    </div>
  );
};

export default MemberSenioritiesForm;
